<template>
    <MainViewContainer>
        <div class="col-span-10 md:col-span-7">
            <h1>{{ $t('default.dashboard.greetings', { name: userName }) }}</h1>
            <div class="mb-40 grid grid-cols-12 gap-1 mt-10">
                <div class="col-span-12 md:col-span-10">
                    <Loader v-if="loading" text="Loading organizations" />
                    <table v-else class="shadow-lg bg-white table-auto w-full">
                        <thead>
                            <tr class="font-bold text-lg">
                                <th class="bg-gray-100 text-left px-2 py-2 md:px-8 md:py-4">
                                    <span v-if="!searchActive">
                                        {{ $t('default.dashboard.survey_table_name') }}
                                        <FontAwesomeIcon
                                            icon="fa-solid fa-search"
                                            class="ml-2 cursor-pointer text-gray-400 hover:text-gray-700"
                                            @click="activateSearch"
                                        />
                                    </span>
                                    <input
                                        v-else
                                        ref="searchInput"
                                        v-model="searchQuery"
                                        type="text"
                                        class="font-normal border border-gray-300 rounded px-2 py-1 w-full"
                                        placeholder="Filter from organizations"
                                        @blur="onSearchBlur"
                                        @keydown.esc="onSearchEsc"
                                    />
                                </th>
                                <th class="bg-gray-100 text-center">{{ $t('default.dashboard.members') }}</th>
                                <th class="bg-gray-100 text-center">{{ $t('default.dashboard.topic2') }}</th>
                                <th class="bg-gray-100"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(org, i) in filteredOrganizations" :key="i" :class="i % 2 ? 'bg-gray-100' : ''">
                                <td class="px-2 py-2 md:px-8 md:py-4 text-green hover:text-green-dark cursor-pointer"
                                    @click="router.push({ name: 'organization', params: { org: org.id } })">
                                    {{ org['name'] }}</td>
                                <td class="py-4 text-center">{{ org['total_members'] }}</td>
                                <td class="py-4 text-center">{{ org['groups'].length }}</td>
                            </tr>
                            <tr v-if="filteredOrganizations.length === 0">
                                <td class="px-2 py-2 md:px-8 md:py-4 text-center">
                                    No organizations found
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </MainViewContainer>
</template>

<script setup>
import MainViewContainer from '@/components/MainViewContainer'
import Loader from '@/components/Loader.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { computed, nextTick, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

const store = useStore()
const router = useRouter()

const user = computed(() => store.state.user.user)
const searchQuery = ref('')
const searchActive = ref(false)
const searchInput = ref(null)

const organizations = computed(() => {
    const all = [...(store.state.organizations.organizations || [])]
    all.sort((a, b) => a.name.localeCompare(b.name, 'fi'))
    return all
})

const filteredOrganizations = computed(() => {
    const q = searchQuery.value.trim().toLowerCase()
    if (!q) return organizations.value
    return organizations.value.filter((org) => org.name.toLowerCase().includes(q))
})

const userName = computed(() => (user.value ? user.value.first_name : ''))

const loading = ref(true)

const onSearchBlur = () => {
    if (!searchQuery.value) {
        searchActive.value = false
    }
}

const onSearchEsc = () => {
    searchQuery.value = ''
    searchActive.value = false
    searchInput.value?.blur()
}

onMounted(async () => {
    if (store.state.organizations.organizations === null) {
        await store.dispatch('organizations/fetchOrganizations')
    }
    loading.value = false
})

const activateSearch = async () => {
    searchActive.value = true
    await nextTick()
    searchInput.value?.focus()
}
</script>
